import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import { tableRowLab } from "../../../style/LapPageStyle";
import UseFetchMedicineAdmin from "../../../helper/UseFetchMedicineAdmin";
import useDebounce from "../../../hooks/UseDebounce";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CustomModal from "../../CustomModal/CustomModal";
import CloseIcon from "@mui/icons-material/Close";
import {
  autoFocusOffWithOutBorder,
  tableCellNewPrescription,
} from "../../../style/PatinetFormStyle";
import { menuItemStyle } from "../../../style/AppointmentStyle";
import UseFetchSearchSaltsPharmacy from "../../../helper/UseFetchSearchSaltsPharmacy";
import UseFetchFormType from "../../../helper/UseFetchFormType";
import {
  primaryBtnTeal,
  rotateSpinner,
  selectStyles,
} from "../../../style/PatientDetailsStyle";
import {
  generateMedicine,
  generateMedicineSalt,
  ExtractMedicineSummaryV1,
} from "../../../connections/postApi";
import { getMedicineSaltPharmacySearch } from "../../../connections/getApi";
import { showToast } from "../../../utils/toastUtil";
import { deleteMedicineAdmin } from "../../../connections/deleteApi";
import ModalDelete from "./ModalDelete";
import { updateMedicine } from "../../../connections/putApi";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import MedicineImageUpload from "./MedicineScanUpload/MedicineImageUpload";
import SearchIcon from "@mui/icons-material/Search";
import zIndex from "@mui/material/styles/zIndex";
import CreatableSelect from "react-select/creatable";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import { normalizeMedicineSalt } from "../../../utils/medicineSaltUtils";

const MedicineListing = () => {
  const [page, setPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(10);
  const [medicineSearchInput, setMedicineSearchInput] = useState(null);
  const [newMedicine, setNewMedicine] = useState(false);
  // const [detailsModal, setDetailsModal] = useState(false);
  const [errors, setErrors] = useState(true);
  const [medSaltInput, setMedSaltInput] = useState(true);
  const [allMedSalt, setAllMedSalt] = useState([]);
  const [formType, setFormType] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [formState, setFormState] = useState({
    name: "",
    medicine_salt: "",
    medicine_salt_id: "",
    form_type: "",
  });
  const [deleteModal, setDeleteModal] = useState(false);
  const [id, setId] = useState(null);
  const [uploadImage, setImageUpload] = useState(false);
  const [autoFillSaltLoading, setAutoFillSaltLoading] = useState(false);
  const [saltInputValue, setSaltInputValue] = useState("");

  const handleSearchMedSalt = (inputValue) => {
    setMedSaltInput(inputValue);
  };

  const handleAutoFillSalt = async () => {
    if (!formState.name?.trim()) return;
    setAutoFillSaltLoading(true);
    try {
      const res = await ExtractMedicineSummaryV1("", "medicine_salt", "GlobalAiContext", null, null, [formState.name]);
      const suggestedSalt = res?.payload?.summaries?.medicines?.[0]?.salt;
      if (!suggestedSalt) { showToast("No salt suggestion available", "INFO"); return; }
      const normalized = normalizeMedicineSalt(suggestedSalt);

      const searchRes = await getMedicineSaltPharmacySearch(normalized, 1, 5);
      const existing = searchRes?.payload?.medicine_salts || [];
      const match = existing.find((s) => s.name?.toLowerCase().trim() === normalized.toLowerCase().trim());

      if (match) {
        setAllMedSalt((prev) =>
          prev.some((p) => p.value === match.id) ? prev : [...prev, { value: match.id, label: match.name }]
        );
        setFormState((prev) => ({ ...prev, medicine_salt_id: match.id, medicine_salt: match.name }));
        setSaltInputValue("");
        showToast(`Salt "${match.name}" found & selected`, "SUCCESS");
      } else {
        const result = await generateMedicineSalt({ medicine_salt: { name: normalized }, medicines_ids: [] });
        if (result?.status?.status === "SUCCESS") {
          const newSalt = result?.payload?.medicine_salt || result?.payload;
          const newId = newSalt?.id || newSalt?.medicine_salt_id || null;
          const newName = newSalt?.name || newSalt?.medicine_salt_name || normalized;
          setAllMedSalt((prev) =>
            prev.some((p) => p.value === newId) ? prev : [...prev, { value: newId || newName, label: newName }]
          );
          setFormState((prev) => ({ ...prev, medicine_salt_id: newId || newName, medicine_salt: newName }));
          setSaltInputValue("");
          showToast(result?.status?.message || "Salt created & selected", "SUCCESS");
        } else {
          showToast(result?.status?.message || "Failed to create salt", "ERROR");
        }
      }
    } catch {
      showToast("Failed to get salt suggestion", "ERROR");
    } finally {
      setAutoFillSaltLoading(false);
    }
  };

  const handleCreateSalt = async (inputValue) => {
    const normalized = normalizeMedicineSalt(inputValue);
    setAutoFillSaltLoading(true);
    try {
      const result = await generateMedicineSalt({ medicine_salt: { name: normalized }, medicines_ids: [] });
      if (result?.status?.status === "SUCCESS") {
        const newSalt = result?.payload?.medicine_salt || result?.payload;
        const newId = newSalt?.id || null;
        const newName = newSalt?.name || normalized;
        setAllMedSalt((prev) =>
          prev.some((p) => p.value === newId) ? prev : [...prev, { value: newId || newName, label: newName }]
        );
        setFormState((prev) => ({ ...prev, medicine_salt_id: newId || newName, medicine_salt: newName }));
        setSaltInputValue("");
        showToast(result?.status?.message || "Salt created successfully", "SUCCESS");
      } else {
        showToast(result?.status?.message || "Failed to create salt", "ERROR");
      }
    } catch {
      showToast("Failed to create salt", "ERROR");
    } finally {
      setAutoFillSaltLoading(false);
    }
  };

  const handleMedicineChange = (e) => {
    e.preventDefault();
    setMedicineSearchInput(e?.target?.value);
  };

  const debouncedInputMedicine = useDebounce(medicineSearchInput, 800);
  const {
    data: medicineList,
    refetch,
    isLoading,
    isFetching,
  } = UseFetchMedicineAdmin(page, itemPerPage, debouncedInputMedicine);

  const debouncedInputMedicineSalt = useDebounce(medSaltInput, 400);
  const { data: medicineSaltList, isLoading: medicineSaltLoading } =
    UseFetchSearchSaltsPharmacy(debouncedInputMedicineSalt, 1, 10);
  const { data: formTypeList } = UseFetchFormType();

  const totalRecords = medicineList?.meta?.total_count || 0;
  const totalPage = Math.ceil(totalRecords / itemPerPage);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  useEffect(() => {
    const data = medicineSaltList?.payload?.medicine_salts || [];
    const formattedMedSalt = data?.map((el) => ({
      value: el?.id,
      label: el?.name,
    }));

    // merge → keep previous + add new unique
    setAllMedSalt((prev) => {
      const merged = [...prev];

      formattedMedSalt.forEach((item) => {
        if (!prev.some((p) => p.value === item.value)) {
          merged.push(item);
        }
      });

      return merged;
    });

    const dataForm = formTypeList?.payload?.form_types || [];
    const formattedFormType = dataForm?.map((el) => ({
      value: el?.value,
      label: el?.label,
    }));
    setFormType(formattedFormType);
  }, [formTypeList, medicineSaltList]);

  const validateForm = () => {
    const newErrors = {};

    if (!formState.name) newErrors.name = "Medicine name is required.";
    if (!formState.form_type) newErrors.form_type = "Form type is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Return true if there are no errors
  };

  // If the user typed/pasted a salt name but never clicked "Create" or picked an
  // option, resolve it on submit: reuse an existing match, otherwise create it.
  const resolveTypedSalt = async () => {
    const typed = saltInputValue?.trim();
    if (!typed) return null;
    const normalized = normalizeMedicineSalt(typed);
    try {
      const searchRes = await getMedicineSaltPharmacySearch(normalized, 1, 5);
      const existing = searchRes?.payload?.medicine_salts || [];
      const match = existing.find(
        (s) => s.name?.toLowerCase().trim() === normalized.toLowerCase().trim(),
      );
      if (match) return { id: match.id, name: match.name };

      const result = await generateMedicineSalt({
        medicine_salt: { name: normalized },
        medicines_ids: [],
      });
      if (result?.status?.status === "SUCCESS") {
        const newSalt = result?.payload?.medicine_salt || result?.payload;
        return {
          id: newSalt?.id || newSalt?.medicine_salt_id || null,
          name: newSalt?.name || newSalt?.medicine_salt_name || normalized,
        };
      }
    } catch {
      // fall through — medicine can still be saved without a salt
    }
    return null;
  };

  const handleCreateMedicine = async (e) => {
    e?.preventDefault();
    if (!validateForm()) {
      return; // Stop submission if validation fails
    }

    setLoading(true);
    try {
      // Resolve a typed-but-not-created salt (leftover typed text) before submit.
      let medicine_salt_id = formState.medicine_salt_id;
      let medicine_salt = formState.medicine_salt;
      if (saltInputValue?.trim()) {
        const resolved = await resolveTypedSalt();
        if (resolved?.id) {
          medicine_salt_id = resolved.id;
          medicine_salt = resolved.name;
        }
      }

      const payload = {
        medicine: {
          ...formState,
          medicine_salt_id,
          medicine_salt,
          discount: formState.discount || 0,
        },
      };

      const result = await generateMedicine(payload);
      // console.log(result);
      showToast(
        result?.status?.message || result?.status?.status,
        result?.status?.status,
      );
      if (result?.status?.status === "SUCCESS") {
        setFormState({
          name: "",
          medicine_salt: "",
          medicine_salt_id: "",
          form_type: "",
        });
        setSaltInputValue("");
      }
      refetch();
      setNewMedicine(false);
    } catch (error) {
      showToast(error.message, "ERROR");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleteModal(false);
    const res = await deleteMedicineAdmin(id);
    showToast(res?.status?.message || res?.status?.status, res?.status?.status);
    if (res?.status?.status) {
      refetch(page, itemPerPage);
    }
  };

  const handleEdit = (medicine) => {
    setIsEdit(true);
    setNewMedicine(true);
    setId(medicine.id);

    const editSalt = {
      value: medicine?.medicine_salt?.id,
      label: medicine?.medicine_salt?.name || "",
    };

    // add only if not present
    setAllMedSalt((prev) => {
      if (!prev.some((p) => p.value === editSalt.value)) {
        return [...prev, editSalt];
      }
      return prev;
    });

    setFormState({
      name: medicine?.name || "",
      medicine_salt: medicine?.medicine_salt?.name,
      medicine_salt_id: medicine?.medicine_salt?.id,
      form_type: medicine?.form_type || "",
    });
  };

  const handleUpdateMedicine = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Resolve a typed-but-not-created salt (leftover typed text) before submit.
      let medicine_salt_id = formState.medicine_salt_id;
      let medicine_salt = formState.medicine_salt;
      if (saltInputValue?.trim()) {
        const resolved = await resolveTypedSalt();
        if (resolved?.id) {
          medicine_salt_id = resolved.id;
          medicine_salt = resolved.name;
        }
      }

      const payload = {
        medicine: {
          ...formState,
          medicine_salt_id,
          medicine_salt,
          discount: formState.discount || 0,
        },
      };

      const result = await updateMedicine(payload, id);
      showToast(
        result?.status?.message || result?.status?.status,
        result?.status?.status,
      );

      if (result?.status?.status === "SUCCESS") {
        setNewMedicine(false);
        setIsEdit(false);
        setFormState({});
        setSaltInputValue("");
        refetch();
      }
    } catch (err) {
      showToast(err.message, "ERROR");
    } finally {
      setLoading(false);
    }
  };

  const isMdUp = useMediaQuery("(min-width:450px)");
  const visiblePageCount = isMdUp ? 3 : 5;

  //  console.log(medicineList?.payload?.medicines);

  return (
    <Box
      sx={{
        width: "100%",
        padding: "10px",
        backgroundColor: "neutral.white",
        px: 2,
      }}
    >
      <Box
        mt={1}
        mb={0}
        marginLeft={1}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        position="sticky"
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            alignItems: "center",
            width: "100%",
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              gap: 2,
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="title_medium" sx={{ fontSize: "18px" }}>
                Medicine List
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                gap: 1,
                alignItems: "center",
              }}
            >
              <AddRoundedIcon
                onClick={() => setNewMedicine(true)}
                sx={{
                  backgroundColor: newMedicine
                    ? "neutral.tealDark"
                    : "neutral.white",
                  p: "4px",
                  width: "24px",
                  color: newMedicine ? "neutral.white" : "neutral.teal",
                  borderRadius: "10px",
                  cursor: "pointer",
                  border: "2px solid #007A99",
                  transition: "0.3s ease",
                  "&:hover": {
                    backgroundColor: "#007A99", // teal
                    color: "neutral.white",
                  },
                }}
              />

              <IconButton
                onClick={() => refetch()}
                sx={{
                  backgroundColor: "#FFFFFF",
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  cursor: "pointer",
                  border: "2px solid #007A99",
                  transition: "0.3s ease",

                  "&:hover": {
                    backgroundColor: "neutral.teal",
                  },
                }}
              >
                <CachedOutlinedIcon
                  sx={{
                    width: "22px",
                    height: "22px",
                    color: "neutral.teal",
                    transition: "0.3s ease",

                    // white color on parent hover
                    ".MuiIconButton-root:hover &": {
                      color: "#FFFFFF",
                    },
                    // animation if loading
                    animation: isFetching
                      ? `${rotateSpinner} 0.9s linear infinite`
                      : "none",
                  }}
                />
              </IconButton>

              {/* <FilterAltOutlinedIcon
                            // onClick={() => setNewMedicine(true)} 
                            sx={{ backgroundColor: "#FFFFFF", p: '4px', width: "24px", color: "neutral.teal", borderRadius: "10px", cursor: "pointer", border: '2px solid #007A99' }} /> */}

              <IconButton
                onClick={() => setImageUpload(true)}
                sx={{
                  backgroundColor: "neutral.white",
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  cursor: "pointer",
                  border: "2px solid #007A99",
                  transition: "0.3s ease",

                  "&:hover": {
                    backgroundColor: "neutral.teal",
                  },
                }}
              >
                <CameraAltOutlinedIcon
                  sx={{
                    width: "22px",
                    height: "22px",
                    color: "neutral.teal",
                    transition: "0.3s ease",
                    ".MuiIconButton-root:hover &": {
                      color: "neutral.white",
                    },
                  }}
                />
              </IconButton>
            </Box>
          </Box>

          <Box>
            <TextField
              sx={{
                ...autoFocusOffWithOutBorder,
                // border: "1px solid #ccc",
                maxWidth: "400px",
                minWidth: "280px",
              }}
              type="text"
              className="form-control"
              placeholder="Search Medicine "
              value={medicineSearchInput}
              onChange={handleMedicineChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>
      </Box>
      <Grid item xs={12} mt={3}>
        <TableContainer sx={{ mt: 1, width: "100%" }}>
          <Table sx={{ width: "100%" }}>
            <TableHead stickyHeader>
              <TableRow
                sx={{
                  alignItems: "center",
                  "& th": {
                    backgroundColor: "#EDEDED",
                  },
                }}
              >
                <TableCell sx={{ borderTopLeftRadius: "16px" }}>
                  <Typography
                    variant="title_small2"
                    color={"#333333"}
                    fontSize={"18px"}
                  >
                    ID
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography
                    variant="title_small2"
                    color={"#333333"}
                    fontSize={"18px"}
                  >
                    Name
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography
                    variant="title_small2"
                    color={"#333333"}
                    fontSize={"18px"}
                  >
                    Medicine Salt
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography
                    variant="title_small2"
                    color={"#333333"}
                    fontSize={"18px"}
                  >
                    Form Type
                  </Typography>
                </TableCell>

                <TableCell sx={{ borderTopRightRadius: "16px" }}>
                  <Typography
                    variant="title_small2"
                    color={"#333333"}
                    fontSize={"18px"}
                  >
                    Actions
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            {isLoading ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={12} sx={{ p: 0 }}>
                    <Box
                      sx={{
                        rowGap: 1.2,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]?.map((item, index) => (
                        <Skeleton
                          key={index}
                          variant="rectangular"
                          sx={{
                            width: "100%",
                            height: "50px",
                            backgroundColor: "#f0f0f0",
                          }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {medicineList?.payload?.medicines?.map((medicine, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      ...tableRowLab,
                      textAlign: "left",
                      backgroundColor: "neutral.white",
                      "& td:last-of-type": {
                        borderTopRightRadius: "12px",
                        borderBottomRightRadius: "12px",
                        textAlign: "right",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        ...tableCellNewPrescription,
                        borderTopLeftRadius: "20px",
                        borderBottomLeftRadius: "20px",
                        width: "30px",
                      }}
                    >
                      <Typography
                        variant="subTitle116px"
                        sx={{ color: "#3A3A3A" }}
                      >
                        {" "}
                        {medicine?.id}{" "}
                      </Typography>
                    </TableCell>

                    <TableCell sx={tableCellNewPrescription}>
                      <Typography
                        variant="subTitle116px"
                        sx={{
                          cursor: "pointer",
                          display: "inline-block",
                          py: 1,
                        }}
                      >
                        {medicine?.name}
                      </Typography>
                    </TableCell>

                    <TableCell sx={tableCellNewPrescription}>
                      <Typography
                        variant="subTitle116px"
                        sx={{
                          color: "#3A3A3A",
                          py: 1,
                        }}
                      >
                        {medicine?.medicine_salt?.name}
                      </Typography>
                    </TableCell>

                    <TableCell sx={tableCellNewPrescription}>
                      <Typography
                        variant="subTitle116px"
                        sx={{ color: "#3A3A3A" }}
                      >
                        {" "}
                        {medicine?.form_type}
                      </Typography>
                    </TableCell>

                    <TableCell
                      sx={{
                        ...tableCellNewPrescription,
                        width: "30px",
                        borderTopRightRadius: "20px",
                        borderBottomRightRadius: "20px",
                      }}
                    >
                      <Box sx={{ display: "flex", gap: "1px" }}>
                        <IconButton onClick={() => handleEdit(medicine)}>
                          <EditNoteOutlinedIcon
                            sx={{ width: "24px", color: "#4A4A4A" }}
                          />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            setDeleteModal(true);
                            setId(medicine?.id);
                          }}
                        >
                          <DeleteForeverOutlinedIcon
                            sx={{ width: "24px", color: "#FB3748" }}
                          />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Grid>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 0px",
          backgroundColor: "neutral.white",
          flexWrap: "wrap",
        }}
      >
        <Typography fontSize="14px">
          Showing {(page - 1) * itemPerPage + 1} -
          {Math.min(page * itemPerPage, totalRecords)} of {totalRecords} entries
        </Typography>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {/* Previous Button */}
          <Button
            onClick={() => page > 1 && setPage(page - 1)}
            startIcon={<ArrowBackIcon sx={{ fontSize: "14px" }} />}
            disabled={page === 1}
            sx={{
              textTransform: "none",
              fontSize: "12px",
              color: page === 1 ? "#9e9e9e" : "#007A99",
              "&:disabled": { color: "#9e9e9e" },
            }}
          >
            Previous
          </Button>

          {/* PAGE NUMBER BUTTONS */}
          {[...Array(totalPage)].slice(0, visiblePageCount).map((_, idx) => {
            const pageNum = idx + 1;
            return (
              <Button
                key={pageNum}
                onClick={() => setPage(pageNum)}
                sx={{
                  minWidth: "30px",
                  height: "30px",
                  fontSize: "12px",
                  borderRadius: "6px",
                  color: page === pageNum ? "#fff" : "#333",
                  backgroundColor: page === pageNum ? "#007A99" : "#fff",
                  border: "1px solid #d0d0d0",
                  "&:hover": {
                    backgroundColor: page === pageNum ? "#336CFB" : "#f2f2f2",
                  },
                }}
              >
                {pageNum}
              </Button>
            );
          })}

          {page > 5 && (
            <Button
              // onClick={() => setPage(pageNum)}
              sx={{
                minWidth: "30px",
                height: "30px",
                fontSize: "12px",
                borderRadius: "6px",
                color: "#fff",
                backgroundColor: "#007A99",
                border: "1px solid #d0d0d0",
                "&:hover": {
                  backgroundColor: "#007A99",
                },
              }}
            >
              {page}
            </Button>
          )}

          {/* Next Button */}
          <Button
            onClick={() => page < totalPage && setPage(page + 1)}
            endIcon={<ArrowForwardIcon sx={{ fontSize: "14px" }} />}
            disabled={page === totalPage}
            sx={{
              textTransform: "none",
              fontSize: "12px",
              color: page === totalPage ? "#9e9e9e" : "#007A99",
              "&:disabled": { color: "#9e9e9e" },
            }}
          >
            Next
          </Button>

          {/* Last Button */}
          {page !== totalPage && (
            <Button
              onClick={() => setPage(totalPage)}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                color: "#336CFB",
              }}
              // disabled={page === totalPage}
            >
              Last &gt;&gt;
            </Button>
          )}

          {/* First  Button */}

          {page !== 1 && (
            <Button
              onClick={() => setPage(1)}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                color: "#336CFB",
              }}
              disabled={page === 1}
            >
              &lt;&lt; First
            </Button>
          )}
        </Box>
      </Box>

      {/* modals */}
      <CustomModal
        customStyles={{
          border: "none",
          width: "85%",
          marginTop: "140px",
          zIndex: 2999,
        }}
        open={newMedicine}
        onClose={() => {
          setNewMedicine(false);
          setFormState({});
          setIsEdit(false);
        }}
      >
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={4}
            position="sticky"
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Typography
                variant="title_medium"
                gutterBottom
                sx={{ fontSize: "20px" }}
              >
                {isEdit ? "Update Medicines" : "Add Medicines"}
              </Typography>
            </Box>
            <IconButton
              sx={{ marginBottom: "r0em" }}
              onClick={() => {
                setNewMedicine(false);
                setFormState({});
                setIsEdit(false);
              }}
            >
              <CloseIcon size="small" sx={{ color: "red" }} />
            </IconButton>
          </Box>

          <Grid container spacing={2}>
            <Grid item sm={3}>
              <TextField
                value={formState?.name}
                fullWidth
                placeholder="Medicine name "
                name="name"
                onChange={(e) => handleFormChange(e)}
                sx={{
                  ...autoFocusOffWithOutBorder,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    height: "50px",
                    padding: "9px !important",
                    "& input": {
                      padding: "8px",
                      fontSize: "14px",
                      fontFamily: "Lato",
                    },
                    "& .MuiAutocomplete-popupIndicator": {
                      display: "none",
                    },
                  },
                }}
                error={!!errors["name"]}
                helperText={errors["name"]}
              />
            </Grid>

            <Grid item sm={5}>
              <Box display="flex" alignItems="center" gap="4px">
                <Box sx={{ flexGrow: 1 }}>
                  <CreatableSelect
                    placeholder="Medicine salt"
                    isClearable
                    isLoading={medicineSaltLoading || autoFillSaltLoading}
                    options={allMedSalt}
                    inputValue={saltInputValue}
                    value={
                      formState.medicine_salt
                        ? { value: formState.medicine_salt_id || formState.medicine_salt, label: formState.medicine_salt }
                        : null
                    }
                    onInputChange={(val, { action }) => {
                      if (action === "input-blur" || action === "menu-close") return;
                      setSaltInputValue(val);
                      handleSearchMedSalt(val);
                    }}
                    onChange={(option) => {
                      setFormState((prev) => ({
                        ...prev,
                        medicine_salt_id: option?.value ?? "",
                        medicine_salt: option?.label ?? "",
                      }));
                    }}
                    onCreateOption={handleCreateSalt}
                    formatCreateLabel={(input) => `Create "${normalizeMedicineSalt(input)}"`}
                    formatOptionLabel={(option, { context }) =>
                      context === "menu" ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {option?.__isNew__ ? (
                            <AddCircleIcon sx={{ fontSize: 18, color: "#1b7f3b" }} />
                          ) : (
                            <ScienceOutlinedIcon sx={{ fontSize: 18, color: "#7a869a" }} />
                          )}
                          <span>{option.label}</span>
                        </Box>
                      ) : (
                        option.label
                      )
                    }
                    styles={{
                      ...selectStyles,
                      control: (base) => ({ ...base, borderRadius: "8px", minHeight: "50px" }),
                      option: (base, state) => ({
                        ...base,
                        cursor: "pointer",
                        ...(state.data?.__isNew__
                          ? {
                              color: "#1b7f3b",
                              fontWeight: 600,
                              backgroundColor: state.isFocused ? "#d6f0df" : "#eafaf0",
                            }
                          : {
                              color: "#3a3a3a",
                              backgroundColor: state.isFocused ? "#f0f7ff" : "#fff",
                            }),
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </Box>
                <Tooltip
                  title={
                    formState.name?.trim()
                      ? "Get medicine salt suggestion from AI"
                      : "Enter medicine name first"
                  }
                  placement="top"
                >
                  <span>
                    <IconButton
                      onClick={handleAutoFillSalt}
                      disabled={!formState.name?.trim() || autoFillSaltLoading}
                      size="small"
                      sx={{
                        color: formState.name?.trim() ? "#2e7d32" : "action.disabled",
                        "&:hover": { backgroundColor: "#e0f4f7", color: "#0097B2" },
                        p: "4px",
                      }}
                    >
                      {autoFillSaltLoading ? (
                        <CircularProgress size={16} sx={{ color: "#2e7d32" }} />
                      ) : (
                        <AutoFixHighIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Grid>

            <Grid item sm={4}>
              <Autocomplete
                clearOnEscape={false}
                clearIcon={null}
                sx={{
                  ...autoFocusOffWithOutBorder,
                  backgroundColor: "neutral.white",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    height: "50px",
                    padding: "9px !important",
                    "& input": {
                      padding: "8px",
                      fontSize: "14px",
                    },
                    "& .MuiAutocomplete-popupIndicator": {
                      display: "none",
                    },
                  },
                }}
                name="form_type"
                options={formType?.filter((option) => option) || []}
                getOptionLabel={(option) => option?.label || ""}
                value={
                  formType?.find(
                    (option) => option.value === formState.form_type,
                  ) || null
                }
                onChange={(e, value) => {
                  setFormState((prev) => ({
                    ...prev,
                    form_type: value?.value || "",
                  }));
                  setErrors((prev) => ({
                    ...prev,
                    form_type: undefined,
                  }));
                }}
                id="form_type"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Form type"
                    error={!!errors.form_type}
                    helperText={errors["form_type"]}
                  />
                )}
                renderOption={(props, option) => (
                  <MenuItem
                    {...props}
                    key={option.value}
                    sx={{ ...menuItemStyle }}
                  >
                    {option.value}
                  </MenuItem>
                )}
                isOptionEqualToValue={(option, value) =>
                  option?.value === value?.value
                }
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              marginTop: 3,
              px: 1,
              width: "100%",
              display: "flex",
              justifyContent: "flex-end", // <-- This aligns the button to the right
            }}
          >
            {/* <Button sx={{ ...primaryBtnTeal, paddingY: "26px", paddingX: '32px' }}
                            onClick={handleCreateMedicine}>
                            <Typography variant='heading2_14' color={'neutral.white'}>Add</Typography>
                        </Button> */}

            <Button
              sx={{ ...primaryBtnTeal }}
              onClick={isEdit ? handleUpdateMedicine : handleCreateMedicine}
              disabled={loading}
            >
              {isEdit ? (
                <Typography variant="heading2_14" color={"neutral.white"}>
                  Update Medicine
                </Typography>
              ) : (
                <Typography variant="heading2_14" color={"neutral.white"}>
                  Add Medicine
                </Typography>
              )}
            </Button>
          </Box>
        </Box>
      </CustomModal>

      <ModalDelete
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        handleDelete={handleDelete}
        id={id}
        label={"Medicine"}
      />

      <CustomModal
        customStyles={{
          border: "none",
          width: "85%",
          marginTop: "20px",
          zIndex: 2999,
        }}
        open={uploadImage}
        onClose={() => {
          setImageUpload(false);
        }}
      >
        <MedicineImageUpload onClose={() => setImageUpload(false)} />
      </CustomModal>
    </Box>
  );
};

export default MedicineListing;
